import React from 'react';
import styled from 'styled-components';
import { crTheme, Link } from '@comicrelief/component-library';

// Styled component for structured error messages
const StructuredErrorContainer = styled.div`
  background-color: ${crTheme.color('coral_light')};
  color: ${crTheme.color('black')};
  font-size: 1rem;
  margin: 1.5rem 0 0 0;
  padding: 1.5rem;
  border-radius: 1rem;
  text-align: center;

  .error-bold {
    font-weight: 700;
    display: block;
    margin-bottom: 0.5rem;
  }

  .error-normal {
    font-weight: 500;
    display: block;
  }
`;

interface StructuredErrorMessageProps {
  errorMessage: string;
  separator?: string;
}

// Helper function to convert "contact us" text to a link
const processTextWithLinks = (text: string): React.ReactNode => {
  const contactUsRegex = /(contact us)/gi;

  if (!contactUsRegex.test(text)) {
    return text;
  }

  const parts = text.split(contactUsRegex);
  let keyCounter = 0;

  return parts.map((part) => {
    keyCounter += 1;
    const key = `part-${keyCounter}`;

    if (part.toLowerCase() === 'contact us') {
      return (
        <Link
          key={key}
          href="https://www.comicrelief.com/contact-us/"
          target="blank"
          rel="noopener noreferrer"
        >
          {part}
        </Link>
      );
    }
    return <span key={key}>{part}</span>;
  });
};

// Utility component to render structured error messages
function StructuredErrorMessage({
  errorMessage,
  separator = '|'
}: StructuredErrorMessageProps) {
  if (!errorMessage.includes(separator)) {
    return (
      <StructuredErrorContainer>
        <span style={{ fontWeight: 'bold' }}>{processTextWithLinks(errorMessage)}</span>
      </StructuredErrorContainer>
    );
  }

  const [boldPart, normalPart] = errorMessage.split(separator);

  return (
    <StructuredErrorContainer data-test="structured-error-message">
      <span className="error-bold">{processTextWithLinks(boldPart)}</span>
      <span className="error-normal">{processTextWithLinks(normalPart)}</span>
    </StructuredErrorContainer>
  );
}

export default StructuredErrorMessage;
